import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { useGetQualifications } from "@src/appV2/Qualifications/api/useGetQualifications";
import { PageHeaderWrapper, Text } from "@src/appV2/redesign/system/ui-components";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { Button } from "../../components/Button";
import { PageFooterWrapper } from "../../components/PageFooterWrapper";
import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { PageWrapper } from "../../components/PageWrapper";
import { AddLicenseForm } from "./Form";
import { type AddLicenseFormData } from "./FormProvider";
import { useAddLicenseFormFieldBehavior } from "./useFormFieldBehavior";

interface AccountRoleDetailsAddLicensePageProps {
  qualificationId: string;
  addQualificationPath: string;
  addStatePath: string;
}

export function AddLicensePage(props: AccountRoleDetailsAddLicensePageProps) {
  const { qualificationId, addQualificationPath, addStatePath } = props;

  const formContext = useFormContext<AddLicenseFormData>();
  const { setValue, watch } = formContext;
  const selectedState = watch("state");

  const { data: qualificationsData } = useGetQualifications({
    enabled: true,
  });

  const currentQualification = qualificationId
    ? qualificationsData?.data.find((qualification) => qualification.id === qualificationId)
    : undefined;

  useEffect(() => {
    if (isDefined(currentQualification?.attributes.displayLabel)) {
      setValue("qualificationName", currentQualification.attributes.displayLabel);
    }
  }, [currentQualification?.attributes.displayLabel, setValue, qualificationId]);

  const fieldBehavior = useAddLicenseFormFieldBehavior({
    currentQualification,
    selectedState,
  });

  const buttonText = fieldBehavior?.licenseNumber.hidden
    ? "Request License Review"
    : "Submit license";

  return (
    <PageWrapper variant="tertiary">
      <PageHeaderWrapper variant="tertiary">
        <PageHeaderTopRow />
      </PageHeaderWrapper>
      <Stack>
        <Stack spacing={4}>
          <Box sx={{ paddingTop: 4, paddingBottom: 6, paddingX: 7 }}>
            <Stack spacing={1}>
              <Text semibold variant="h4">
                Add license
              </Text>
              <Text
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                }}
              >
                We&apos;ll verify your license with the chosen state and let you know once it has
                been accepted.
              </Text>
            </Stack>
          </Box>
          <AddLicenseForm
            qualificationId={qualificationId}
            addQualificationPath={addQualificationPath}
            addStatePath={addStatePath}
            formContext={{
              selectedState,
              fieldBehavior,
              ...formContext,
            }}
          />
        </Stack>
      </Stack>
      <PageFooterWrapper>
        <Button fullWidth size="large" variant="contained" type="submit">
          {buttonText}
        </Button>
      </PageFooterWrapper>
    </PageWrapper>
  );
}
